import { css } from '@emotion/react';
import { Button, Link, TextInput, atoms } from '@zapier/marketing-ui';
import { addParamsToUrl, removeParamsFromUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';

import { CodeType, RouteTo, QueryParams } from '../../types';
import { SensitiveData } from '../global/SensitiveData';
import { useTwoFactor, useAutoFocus } from '../../hooks';

export type TwoFactorAuthenticationFormProps = {
  /**
   * Url of page to redirect user to after successfully logging in
   */
  next?: string | undefined;
  /**
   * Current query params (so they can be preserved when routing)
   */
  queryParams?: QueryParams;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: RouteTo;
  /**
   * true to show recovery code form
   */
  useRecoveryCode?: boolean;
};

const styles = {
  root: css`
    display: grid;
    grid-gap: 20px;
  `,
  links: css`
    display: grid;
    grid-gap: 12px;
  `,
};

export function TwoFactorAuthenticationForm({
  next,
  queryParams = {},
  routeTo,
  useRecoveryCode = false,
}: TwoFactorAuthenticationFormProps) {
  const codeType: CodeType = useRecoveryCode ? 'recovery_code' : 'code';

  const { canSubmit, code, error, handleChangeCode, handleSubmit, status } =
    useTwoFactor({
      codeType,
      next,
      routeTo,
    });

  const fieldLabel =
    codeType === 'code' ? 'Authentication Code' : 'Recovery Code';
  const fieldId = codeType === 'code' ? '2fa-code' : '2fa-recovery-code';
  const fieldHint =
    codeType === 'code'
      ? 'Enter the 6-digit code generated by your authentication app.'
      : 'Enter a recovery code that you saved when setting up 2FA.';

  // Used to focus the code field when we show the page.
  // We need 2 different ref to keep the autofocus on the field when we switch
  // from Authentication code to Recovery code and viceversa.
  const authCodeRef = useAutoFocus(codeType === 'code');
  const recoveryCodeRef = useAutoFocus(codeType !== 'code');

  return (
    <form css={styles.root} onSubmit={handleSubmit}>
      <SensitiveData>
        <TextInput
          ref={codeType === 'code' ? authCodeRef : recoveryCodeRef}
          id={fieldId}
          name={fieldId}
          label={fieldLabel}
          type="text"
          placeholder={fieldLabel}
          // aria-label carries the hint: marketing-ui's TextInput overwrites a caller's aria-describedby.
          // TODO: switch to aria-description once assistive-technology support is confirmed.
          aria-label={`${fieldLabel}. ${fieldHint}`}
          description={fieldHint}
          value={code}
          required={true}
          disabled={status !== 'initial'}
          autoComplete="one-time-code"
          inputMode="numeric"
          onChange={value => handleChangeCode(value)}
        />
      </SensitiveData>
      <Button
        fullWidth={true}
        variant="brand"
        disabled={!canSubmit || status !== 'initial'}
        type="submit"
      >
        Confirm
      </Button>
      {error && (
        <div
          className={`${atoms.bodySM} ${atoms.textError} ${atoms.textCenter}`}
        >
          {error}
        </div>
      )}
      <div
        className={`${atoms.bodySM} ${atoms.textCenter} ${atoms.textNeutral800}`}
        css={styles.links}
      >
        {codeType === 'code' && (
          <Link
            href={addParamsToUrl(urls.TWO_FACTOR_URL, {
              ...queryParams,
              use_recovery_code: true,
            })}
          >
            Use a recovery code
          </Link>
        )}
        {codeType === 'recovery_code' && (
          <Link
            href={addParamsToUrl(urls.TWO_FACTOR_URL, {
              ...queryParams,
              use_recovery_code: false,
            })}
          >
            Use a 2FA code
          </Link>
        )}
        <Link
          href={removeParamsFromUrl(
            addParamsToUrl(urls.LOGIN_URL, queryParams),
            ['use_recovery_code']
          )}
        >
          Log in as a different user
        </Link>
      </div>
    </form>
  );
}
